import { useState, useEffect } from "react";
import duckIcon from "@/assets/duck-icon.svg";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Home,
  Trophy,
  Gift,
  Store as StoreIcon,
  Target,
  Ticket,
  
  Gamepad2,
  Twitter,
  MessageCircle,
  Youtube,
  Send,
  Users,
  User as UserIcon,
  
  Settings,
  Shield,
  TrendingUp,
  BookOpen,
  ChevronDown,
  ExternalLink,
  Clock,
  Video,
  Swords,
  CalendarCheck,
  CalendarDays,
  LifeBuoy,
  Newspaper,
  ListMusic,
} from "lucide-react";
import logo from "@/assets/logo.png";
import { PartnerLogo } from "@/components/PartnerLogo";
import { PARTNER } from "@/config/partner";
import hypedropLogo from "@/assets/hypedrop-icon.png";
import kickLogo from "@/assets/kick-logo.png";
import discordIcon from "@/assets/discord-icon.png";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { fetchMyMonthlyWager } from "@/lib/monthlyWager";
import { User, Session } from "@supabase/supabase-js";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Main navigation items
const mainNavItems = [
  {
    title: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: "Leaderboards",
    url: "/leaderboards",
    icon: Trophy,
  },
  {
    title: "Giveaways",
    url: "/giveaways",
    icon: Gift,
  },
  {
    title: "Challenges",
    url: "/challenges",
    icon: Gamepad2,
  },
  {
    title: "About Us",
    url: "/about",
    icon: Users,
  },
  {
    title: "Support",
    url: "/support",
    icon: LifeBuoy,
  },
];

// Gamdom sub-items
const gamdomSubItems = [
  {
      title: "Rainbet Guide",
      url: "/rainbet-guide",
    icon: BookOpen,
    comingSoon: false,
  },
  {
    title: "$30K LEADERBOARD",
    url: "/rainbet-leaderboard",
    icon: Trophy,
    comingSoon: false,
  },
  {
    title: "Monthly Milestones",
    url: "/milestones",
    icon: TrendingUp,
    comingSoon: false,
  },
];

const hypedropSubItems = [
  {
    title: "€10K LEADERBOARD",
    url: "/hypedrop-leaderboard",
    icon: Trophy,
  },
  {
    title: "Sponsored Battles",
    url: "/sponsored-battles",
    icon: Swords,
  },
];

// Stream sub-items
const streamSubItems = [
  {
    title: "Daily Login",
    url: "/daily-login",
    icon: CalendarCheck,
  },
  {
    title: "TIERS",
    url: "/tiers",
    icon: Shield,
  },
  {
    title: "Points Store",
    url: "/store",
    icon: StoreIcon,
  },
  {
    title: "Balance Guess",
    url: "/balance-guess",
    icon: Target,
  },
  {
    title: "Promo Codes",
    url: "/promo-codes",
    icon: Ticket,
  },
  {
    title: "News",
    url: "/news",
    icon: Newspaper,
  },
  {
    title: "Calendar",
    url: "/calendar",
    icon: CalendarDays,
    adminOnly: true,
  },
  {
    title: "Slot Requests",
    url: "/slot-requests",
    icon: ListMusic,
  },
];
interface Milestone {
  id: string;
  wager_required: number;
  reward_value: number | null;
}
export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentPlatform = searchParams.get("platform") || "gamdom";
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Section open states
  const [gamdomOpen, setGamdomOpen] = useState(true);
  const [hypedropOpen, setHypedropOpen] = useState(true);
  const [streamOpen, setStreamOpen] = useState(true);

  // Wager milestone state
  const [userWager, setUserWager] = useState<number>(0);
  const [nextMilestone, setNextMilestone] = useState<Milestone | null>(null);
  const [wagerLoading, setWagerLoading] = useState(false);
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setIsAdmin(false);
        setUserWager(0);
        setNextMilestone(null);
        return;
      }

      // Check admin status
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id);
      setIsAdmin(roles?.some((r) => r.role === "admin") ?? false);

      // Fetch wager data
      setWagerLoading(true);
      try {
        // Fetch milestones
        const { data: milestonesData } = await supabase
          .from("reward_milestones")
          .select("id, wager_required, reward_value")
          .order("wager_required", {
            ascending: true,
          });

        const monthlyWager = await fetchMyMonthlyWager();
        setUserWager(monthlyWager);
        if (milestonesData) {
          const next = milestonesData.find((m) => monthlyWager < m.wager_required);
          setNextMilestone(next || milestonesData[milestonesData.length - 1] || null);
        }
      } catch (error) {
        console.error("Error fetching wager data:", error);
      } finally {
        setWagerLoading(false);
      }
    };
    if (user) {
      fetchUserData();
    }
  }, [user]);
  const isActive = (path: string) => {
    if (path.includes("?")) {
      return location.pathname + location.search === path;
    }
    return location.pathname === path;
  };
  const getProgressPercentage = () => {
    if (!nextMilestone) return 0;
    return Math.min((userWager / nextMilestone.wager_required) * 100, 100);
  };
  const formatCurrency = (amount: number) => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}k`;
    }
    return `$${amount.toLocaleString()}`;
  };
  const formatExactCurrency = (amount: number) =>
    `$${Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar-background [&>*]:bg-sidebar-background">
      <SidebarHeader className="p-4 h-[72px] flex items-center border-b border-sidebar-border bg-sidebar-background">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
          <img src={logo} alt="GamDucks Logo" className="h-12 w-auto" style={{ aspectRatio: '501 / 98' }} />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2 bg-sidebar-background uppercase font-bold">
        {user && (
          <div
            className="p-3 mx-1 rounded-lg bg-sidebar-accent border border-sidebar-border cursor-pointer hover:bg-sidebar-accent/80 transition-colors"
            onClick={() => navigate("/profile")}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-xs font-bold text-foreground">Monthly Wager</span>
              </div>
              {nextMilestone && (
                <span className="text-xs text-primary font-bold">${nextMilestone.reward_value || 0}</span>
              )}
            </div>
            <Progress value={getProgressPercentage()} className="h-2.5 mb-2" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-primary font-bold">{formatExactCurrency(userWager)}</span>
              {nextMilestone && (
                <span className="text-muted-foreground font-bold">
                  / {formatCurrency(nextMilestone.wager_required)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* All Navigation */}
        <SidebarGroup className="mt-2">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0">
              {/* Main Nav Items */}
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/"}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sidebar-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
                      activeClassName="bg-primary/10 text-primary font-medium"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Gamdom */}
              <SidebarMenuItem>
                <Collapsible open={gamdomOpen} onOpenChange={setGamdomOpen}>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-white hover:text-white/80 hover:bg-muted/50 transition-colors text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setGamdomOpen(!gamdomOpen);
                      }}
                    >
                      <div className="h-4 w-4 flex items-center justify-center shrink-0 rounded-[3px] overflow-hidden">
                        <PartnerLogo variant="icon" className="h-full w-full object-contain" />
                      </div>
                      <span>{PARTNER.name}</span>
                      <ChevronDown
                        className={`ml-auto h-3 w-3 transition-transform duration-200 ${gamdomOpen ? "rotate-180" : ""}`}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-3 space-y-0">
                      <a
                        href={PARTNER.affiliateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-2 py-1 rounded-md text-xs text-white hover:bg-white/10 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        <span>VISIT {PARTNER.displayName}</span>
                      </a>
                      {gamdomSubItems.map((item) => (
                        <SidebarMenuButton key={item.title} asChild disabled={item.comingSoon}>
                          {item.comingSoon ? (
                            <div className="flex items-center gap-2 px-2 py-1 rounded-md text-xs text-sidebar-foreground/50 cursor-not-allowed">
                              <item.icon className="h-3 w-3 shrink-0" />
                              <span className="truncate">{item.title}</span>
                              <span className="ml-auto flex items-center gap-0.5 text-[8px] bg-muted/50 px-1 py-0.5 rounded-full">
                                <Clock className="h-2 w-2" />
                                Soon
                              </span>
                            </div>
                          ) : (
                            <NavLink
                              to={item.url}
                              className={`flex items-center gap-2 px-2 py-1 rounded-md text-xs transition-colors ${isActive(item.url) ? "bg-white/10 text-white font-medium" : "text-sidebar-foreground hover:text-foreground hover:bg-muted/50"}`}
                            >
                              <item.icon className="h-3 w-3 shrink-0" />
                              <span className="truncate">{item.title}</span>
                            </NavLink>
                          )}
                        </SidebarMenuButton>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>

              {/* HypeDrop */}
              <SidebarMenuItem>
                <Collapsible open={hypedropOpen} onOpenChange={setHypedropOpen}>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-white hover:text-white/80 hover:bg-muted/50 transition-colors text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setHypedropOpen(!hypedropOpen);
                      }}
                    >
                      <div className="h-4 w-4 rounded overflow-hidden flex items-center justify-center">
                        <img
                          alt="HypeDrop"
                          className="h-full w-full object-contain"
                          src="/lovable-uploads/cbfb82bd-5a69-4cd3-9776-76c358af4b74.png"
                        />
                      </div>
                      <span>HypeDrop</span>
                      <ChevronDown
                        className={`ml-auto h-3 w-3 transition-transform duration-200 ${hypedropOpen ? "rotate-180" : ""}`}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-3 space-y-0">
                      <a
                        href="https://hypedrop.com/r/GAMDUCKS"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-2 py-1 rounded-md text-xs text-white hover:bg-white/10 transition-colors"
                      >
                        <ExternalLink className="h-3 w-3" />
                        <span>Visit HypeDrop</span>
                      </a>
                      {hypedropSubItems.map((item) => (
                        <SidebarMenuButton key={item.title} asChild>
                            <NavLink
                              to={item.url}
                              className={`flex items-center gap-2 px-2 py-1 rounded-md text-xs transition-colors ${isActive(item.url) ? "bg-white/10 text-white font-medium" : "text-sidebar-foreground hover:text-foreground hover:bg-muted/50"}`}
                            >
                              <item.icon className="h-3 w-3 shrink-0" />
                              <span className="truncate">{item.title}</span>
                            </NavLink>
                        </SidebarMenuButton>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>

              {/* Stream */}
              <SidebarMenuItem>
                <Collapsible open={streamOpen} onOpenChange={setStreamOpen}>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sidebar-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        setStreamOpen(!streamOpen);
                      }}
                    >
                      <img src={duckIcon} alt="Stream" className="h-4 w-4 shrink-0 object-contain" />
                      <span>STREAM</span>
                      <ChevronDown
                        className={`ml-auto h-3 w-3 transition-transform ${streamOpen ? "rotate-180" : ""}`}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-3 space-y-0">
                      {streamSubItems
                        .filter((item) => !(item as { adminOnly?: boolean }).adminOnly || isAdmin)
                        .map((item) => (
                        <SidebarMenuButton key={item.title} asChild>
                          <NavLink
                            to={item.url}
                            className={`flex items-center gap-2 px-2 py-1 rounded-md text-xs transition-colors ${isActive(item.url) ? "bg-primary/10 text-primary font-medium" : "text-sidebar-foreground hover:text-foreground hover:bg-muted/50"}`}
                          >
                            <item.icon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Section */}
        {isAdmin && (
          <SidebarGroup className="mt-4">
            <SidebarGroupLabel className="text-xs text-sidebar-foreground uppercase tracking-wider px-2">
              Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin"
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sidebar-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
                      activeClassName="bg-primary/10 text-primary font-medium"
                    >
                      <Settings className="h-4 w-4 shrink-0" />
                      <span>Admin Panel</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/store-admin"
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sidebar-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
                      activeClassName="bg-primary/10 text-primary font-medium"
                    >
                      <StoreIcon className="h-4 w-4 shrink-0" />
                      <span>Store Management</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Social Links */}
        <SidebarGroup className="mt-auto pt-4">
          <SidebarGroupLabel className="text-xs text-sidebar-foreground uppercase tracking-wider px-2">
            Socials
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="grid grid-cols-5 gap-3 px-2 py-2">
              <a href="https://kick.com/gamducks" target="_blank" rel="noopener noreferrer" title="Kick">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Kick"
                  className="rounded-full h-9 w-9 hover:bg-primary/10 transition-all"
                >
                  <img src={kickLogo} alt="Kick" className="h-5 w-5 rounded-lg brightness-0 invert opacity-80" />
                </Button>
              </a>
              <a href="https://discord.gg/gamducks" target="_blank" rel="noopener noreferrer" title="Discord">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Discord"
                  className="rounded-full h-9 w-9 hover:bg-primary/10 transition-all"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                </Button>
              </a>
              <a href="https://x.com/gamducks" target="_blank" rel="noopener noreferrer" title="X">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="X"
                  className="rounded-full h-9 w-9 hover:bg-primary/10 transition-all"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </Button>
              </a>
              <a href="https://www.youtube.com/@gamducksog" target="_blank" rel="noopener noreferrer" title="YouTube">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="YouTube"
                  className="rounded-full h-9 w-9 hover:bg-primary/10 transition-all"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </Button>
              </a>
              <a href="https://t.me/gamducksrewards" target="_blank" rel="noopener noreferrer" title="Telegram">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Telegram"
                  className="rounded-full h-9 w-9 hover:bg-primary/10 transition-all"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </Button>
              </a>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 border-t border-sidebar-border bg-sidebar-background uppercase font-bold">
        {user ? (
          <div className="space-y-2">
            <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => navigate("/profile")}>
              <UserIcon className="h-5 w-5" />
              <span>Profile</span>
            </Button>
          </div>
        ) : (
          <Button onClick={() => navigate("/auth")} className="w-full bg-primary hover:bg-primary/90">
            Sign In
          </Button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
export default AppSidebar;
